import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import SEO from '../components/SEO';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { EmptyState } from '../components/EmptyState';

const GROUPS = [
  {
    heading: 'Orders & Shipping',
    items: [
      { q: 'When will my order ship?', a: 'Orders placed before 12pm ET dispatch the same business day from our New York atelier. Bespoke and made-to-order pieces ship in 2–3 weeks — we\'ll email you a tracking link the moment yours is on its way.' },
      { q: 'Do you ship internationally?', a: 'Yes. We ship to over 60 countries. Duties and taxes are calculated at checkout so there are no surprises on arrival.' },
      { q: 'Is shipping complimentary?', a: 'Standard shipping is complimentary on all orders over $150 within the United States, and over $300 internationally.' },
      { q: 'Can I collect an order in store?', a: 'Yes. Choose "Atelier pick-up" at checkout and select New York, Kyoto, or Copenhagen. Your order will be waiting within one business day.' },
      { q: 'Can I change or cancel an order?', a: 'Please write to us within one hour of placing your order. After that we may not be able to change it — but we\'ll always try.' },
    ],
  },
  {
    heading: 'Returns & Exchanges',
    items: [
      { q: 'What is your return policy?', a: 'Unworn pieces with original tags may be returned within 30 days of delivery for a full refund. Sale pieces are eligible for exchange or credit within 14 days.' },
      { q: 'How do I start a return?', a: 'Write to hello@evermark.com with your order number and we\'ll send you a prepaid label and instructions.' },
      { q: 'Do you repair or alter garments?', a: 'Yes — for the life of the garment. We offer complimentary hemming for the first year, and small repairs for the life of the piece at our workshop.' },
      { q: 'When will I be refunded?', a: 'Refunds are issued to the original payment method within 5 business days of the return arriving at our warehouse.' },
    ],
  },
  {
    heading: 'Sizing & Fit',
    items: [
      { q: 'How do your sizes run?', a: 'Our sizing follows European fit — trim through the shoulder, considered through the body. Consult the size guide on each product page and, when in doubt, size up for outerwear and down for knitwear.' },
      { q: 'Do you offer a fit consultation?', a: 'We do. Book a virtual or in-person appointment via the Contact page and one of our advisors will walk you through the collection.' },
      { q: 'Where can I find measurements?', a: 'Each product page includes a "Size guide" dialog with body-and-garment measurements in inches and centimetres.' },
    ],
  },
  {
    heading: 'Care & Craft',
    items: [
      { q: 'How should I care for my Evermark pieces?', a: 'Each garment ships with a small card of care instructions. As a rule: wash cold, dry flat, iron gently. Fine merino and cashmere respond to hand washing and shaping.' },
      { q: 'Where are your clothes made?', a: 'Across trusted mills and workshops in Italy (Biella, Prato), Portugal (Porto), Japan (Osaka), and Scotland. Country of origin is listed on every product page.' },
      { q: 'Are your fabrics sustainable?', a: 'We work with natural fibres wherever possible, favour mills with third-party certifications (OEKO-TEX, GOTS, RWS), and design in small runs to avoid excess. Full transparency on our sourcing is available on request.' },
      { q: 'What is the Ten-Year Mark?', a: 'Our quality promise: if a piece fails from a manufacturing fault within ten years, we\'ll repair or replace it. See the About page for the full commitment.' },
    ],
  },
  {
    heading: 'Account & Wishlist',
    items: [
      { q: 'Do I need an account to shop?', a: 'No. You may check out as a guest at any time. An account simply saves your addresses, order history, and wishlist between visits.' },
      { q: 'Where is my wishlist saved?', a: 'For now, on this device. Once accounts launch, your wishlist will follow you across every device you sign in on.' },
      { q: 'Can I share my wishlist?', a: 'Sharing via URL is coming with the accounts release. For now, take a screenshot — it\'s more considered anyway.' },
    ],
  },
];

// Flatten for search
const ALL_ITEMS = GROUPS.flatMap((g) => g.items.map((it, i) => ({ ...it, group: g.heading, gi: i })));

export default function FAQ() {
  const [query, setQuery] = useState('');
  const [openItems, setOpenItems] = useState([]);

  const filteredGroups = useMemo(() => {
    if (!query.trim()) return GROUPS;
    const q = query.toLowerCase();
    return GROUPS.map((g) => ({
      ...g,
      items: g.items.filter((it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)),
    })).filter((g) => g.items.length > 0);
  }, [query]);

  const totalHits = filteredGroups.reduce((s, g) => s + g.items.length, 0);

  // When searching, auto-expand every matching item so highlighted terms
  // are visible. When the query is cleared, collapse everything.
  useEffect(() => {
    if (query.trim()) {
      const next = filteredGroups.flatMap((g) => g.items.map((_, i) => `${g.heading}-${i}`));
      setOpenItems(next);
    } else {
      setOpenItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const highlight = (text) => {
    if (!query.trim()) return text;
    const parts = text.split(new RegExp(`(${escapeRegex(query)})`, 'gi'));
    return parts.map((p, i) =>
      p.toLowerCase() === query.toLowerCase()
        ? <mark key={i} className="bg-foreground/10 text-foreground rounded-sm px-0.5">{p}</mark>
        : <span key={i}>{p}</span>
    );
  };

  return (
    <div data-testid="page-faq" className="container py-16 md:py-24">
      <SEO
        title="FAQ"
        description="Frequently asked questions on orders, shipping, returns, sizing, care, and craft."
        path="/faq"
        breadcrumb={[{ to: '/', label: 'Home' }, { label: 'FAQ' }]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: GROUPS.flatMap((g) => g.items.map((it) => ({
            '@type': 'Question',
            name: it.q,
            acceptedAnswer: { '@type': 'Answer', text: it.a },
          }))),
        }}
      />
      <Breadcrumb items={[{ to: '/', label: 'Home' }, { label: 'FAQ' }]} className="mb-8" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <p className="overline text-muted-foreground">Questions, considered</p>
        <h1 className="mt-3 font-display text-6xl md:text-8xl leading-[0.9]">Frequently Asked.</h1>
        <p className="mt-6 max-w-xl text-muted-foreground text-lg leading-relaxed">
          The answers we're asked most often. Can't find yours?{' '}
          <Link to="/contact" className="link-underline">Write to us.</Link>
        </p>
      </motion.div>

      {/* Search */}
      <div className="mt-10 flex items-center gap-3 border-b border-foreground pb-3 max-w-xl">
        <Search className="h-4 w-4 opacity-60" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="faq-search"
          placeholder="Search the questions — shipping, cashmere, returns…"
          className="flex-1 bg-transparent outline-none py-2 text-base placeholder:text-muted-foreground/50"
        />
        {query && (
          <button aria-label="Clear search" onClick={() => setQuery('')} className="opacity-60 hover:opacity-100" data-testid="faq-search-clear">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {query && (
        <p className="mt-3 overline text-muted-foreground" data-testid="faq-result-count">
          {totalHits === 0 ? 'No matches' : `${totalHits} ${totalHits === 1 ? 'match' : 'matches'} for "${query}"`}
        </p>
      )}

      <div className="mt-16 grid gap-16 md:grid-cols-[240px_1fr]">
        <aside className="hidden md:block">
          <nav aria-label="FAQ sections" className="sticky top-32">
            <p className="overline text-muted-foreground mb-4">Contents</p>
            <ul className="space-y-3">
              {(query ? filteredGroups : GROUPS).map((g) => (
                <li key={g.heading}>
                  <a href={`#${slug(g.heading)}`} className="text-sm link-underline">
                    {g.heading} {query && <span className="overline text-muted-foreground">({g.items.length})</span>}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <div className="space-y-16">
          {filteredGroups.length === 0 ? (
            <EmptyState
              eyebrow="Nothing yet"
              title={`No answers match "${query}".`}
              description="Try a different word — or write to us directly."
              action={<Link to="/contact" className="overline link-underline">Contact the studio →</Link>}
              testId="faq-empty"
            />
          ) : (
            filteredGroups.map((g) => (
              <section key={g.heading} id={slug(g.heading)}>
                <h2 className="font-display text-3xl md:text-4xl mb-6">{g.heading}</h2>
                <Accordion
                  type="multiple"
                  value={openItems}
                  onValueChange={setOpenItems}
                  className="border-t border-border"
                >
                  {g.items.map((it, i) => (
                    <AccordionItem key={i} value={`${g.heading}-${i}`}>
                      <AccordionTrigger data-testid={`faq-trigger-${slug(g.heading)}-${i}`}>
                        {highlight(it.q)}
                      </AccordionTrigger>
                      <AccordionContent>{highlight(it.a)}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function slug(s) { return s.toLowerCase().replace(/[^a-z]+/g, '-'); }
function escapeRegex(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
